import { ActionIcon, Badge, Group, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import type { AdminListUsersQuery } from '@src/__generated__/graphql';
import { IconCopy, IconMail } from '@tabler/icons-react';

type AdminUserSummary = AdminListUsersQuery['adminListUsers']['items'][number];

const statusColors: Record<string, string> = {
  active: 'green',
  inactive: 'gray',
  confirmed: 'green',
  force_change_password: 'orange',
  unconfirmed: 'yellow',
  archived: 'gray',
  unknown: 'gray',
};

const billingStatusColors: Record<string, string> = {
  active: 'green',
  trialing: 'blue',
  'past-due': 'red',
  unpaid: 'orange',
  canceled: 'gray',
  unknown: 'gray',
};

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return parsedDate.toLocaleString();
};

const renderConfirmStatusBadge = (record: AdminUserSummary) => (
  <Badge color={statusColors[record.confirmStatus?.toLowerCase() || 'unknown'] || 'gray'} variant="light">
    {record.confirmStatus || '-'}
  </Badge>
);

const renderBillingStatusBadge = (record: AdminUserSummary) => (
  <Badge color={billingStatusColors[record.billingStatus?.toLowerCase() || 'unknown'] || 'gray'} variant="light">
    {record.billingStatus || '-'}
  </Badge>
);

interface AdminUserExpandedDetailsProps {
  record: AdminUserSummary;
  copiedKey: string | null;
  onCopy: (key: string, value?: string | null) => void | Promise<void>;
}

function getCopyLabel(hasValue: boolean, isCopied: boolean) {
  if (!hasValue) return 'No value';
  return isCopied ? 'Copied!' : 'Copy';
}

function AdminUserExpandedDetails({ record, copiedKey, onCopy }: AdminUserExpandedDetailsProps) {
  const firstLast = [record.givenName, record.familyName].filter(Boolean).join(' ').trim() || '-';
  const emailValue = record.email || '';
  const billingCustomerId = record.billingCustomerExternalId || '';
  const billingSubscriptionId = record.billingSubscriptionExternalId || '';
  const usernameCopyKey = `${record.username}:username`;
  const emailCopyKey = `${record.username}:email`;
  const customerCopyKey = `${record.username}:customer`;
  const subscriptionCopyKey = `${record.username}:subscription`;

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" verticalSpacing="xs" p="md">
      <Stack gap="xs">
        <Group gap="xs">
          <Text fw={600} size="sm">
            Username:
          </Text>
          <Text size="sm">{record.username || '-'}</Text>
          <Tooltip label={getCopyLabel(Boolean(record.username), copiedKey === usernameCopyKey)}>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label={`Copy username for ${record.username}`}
              onClick={() => void onCopy(usernameCopyKey, record.username)}
            >
              <IconCopy size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            First Last:
          </Text>
          <Text size="sm">{firstLast}</Text>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Display Name:
          </Text>
          <Text size="sm">{record.displayName || '-'}</Text>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Email:
          </Text>
          <Text size="sm">{emailValue || '-'}</Text>
          <Tooltip label={getCopyLabel(Boolean(emailValue), copiedKey === emailCopyKey)}>
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={!emailValue}
              aria-label={`Copy email for ${record.username}`}
              onClick={() => void onCopy(emailCopyKey, emailValue)}
            >
              <IconCopy size={14} />
            </ActionIcon>
          </Tooltip>
          {emailValue ? (
            <Tooltip label="Send email">
              <ActionIcon
                component="a"
                href={`mailto:${emailValue}`}
                variant="subtle"
                size="sm"
                aria-label={`Email ${record.username}`}
              >
                <IconMail size={14} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Tooltip label="No value">
              <ActionIcon variant="subtle" size="sm" disabled aria-label={`Email ${record.username}`}>
                <IconMail size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Created at:
          </Text>
          <Text size="sm">{formatDate(record.createdAt)}</Text>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Updated At:
          </Text>
          <Text size="sm">{formatDate(record.lastModifiedAt)}</Text>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Last Seen At:
          </Text>
          <Text size="sm">{formatDate(record.lastSeenAt)}</Text>
        </Group>
      </Stack>

      <Stack gap="xs">
        <Group gap="xs">
          <Text fw={600} size="sm">
            Status:
          </Text>
          <Badge color={record.enabled ? 'green' : 'red'} variant="light">
            {record.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Confirm Status:
          </Text>
          {renderConfirmStatusBadge(record)}
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Email Verified:
          </Text>
          <Text size="sm">{record.markEmailVerified ? 'Yes' : 'No'}</Text>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Billing Status:
          </Text>
          {renderBillingStatusBadge(record)}
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Trial Ends At:
          </Text>
          <Text size="sm">{formatDate(record.trialEndsAt)}</Text>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Current Period End At:
          </Text>
          <Text size="sm">{formatDate(record.currentPeriodEndAt)}</Text>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Cancel At Period End:
          </Text>
          <Badge color={record.cancelAtPeriodEnd ? 'orange' : 'green'} variant="light">
            {record.cancelAtPeriodEnd ? 'TRUE' : 'FALSE'}
          </Badge>
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Stripe Customer Id:
          </Text>
          <Text size="sm">{billingCustomerId || '-'}</Text>
          {billingCustomerId ? (
            <Tooltip label={getCopyLabel(true, copiedKey === customerCopyKey)}>
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label={`Copy Stripe customer id for ${record.username}`}
                onClick={() => void onCopy(customerCopyKey, billingCustomerId)}
              >
                <IconCopy size={14} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
        <Group gap="xs">
          <Text fw={600} size="sm">
            Stripe Subscription Id:
          </Text>
          <Text size="sm">{billingSubscriptionId || '-'}</Text>
          {billingSubscriptionId ? (
            <Tooltip label={getCopyLabel(true, copiedKey === subscriptionCopyKey)}>
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label={`Copy Stripe subscription id for ${record.username}`}
                onClick={() => void onCopy(subscriptionCopyKey, billingSubscriptionId)}
              >
                <IconCopy size={14} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      </Stack>
    </SimpleGrid>
  );
}

export default AdminUserExpandedDetails;
